import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { resolveImageUrl } from '@/utils/imageUrl';
import { useApplyContext } from '@/lib/smartApply';
import { usePlatformFlags } from '@/hooks/usePlatformFlags';
import { roleFromToken, isAiChatAllowed } from '@/lib/roleGate';
import axios from 'axios';
import {
  Home, Compass, ListTodo, Bot, Bookmark, Send, TrendingUp, Search,
  GraduationCap, Building2, FileText, Briefcase, ShieldCheck, Lightbulb,
  Shield, Users, MessageSquare, BookOpen
} from 'lucide-react';

const SERVICE_ICONS = {
  courses: GraduationCap, colleges: Building2, visa: FileText, jobs: Briefcase,
  rpl: ShieldCheck, skills: Lightbulb, insurance: Shield, rentals: Home, agents: Users,
};
const SERVICE_COLORS = {
  courses: 'text-blue-600 bg-blue-50', colleges: 'text-violet-600 bg-violet-50',
  visa: 'text-rose-600 bg-rose-50', jobs: 'text-emerald-600 bg-emerald-50',
  rpl: 'text-amber-600 bg-amber-50', skills: 'text-cyan-600 bg-cyan-50',
  insurance: 'text-indigo-600 bg-indigo-50', rentals: 'text-orange-600 bg-orange-50',
  agents: 'text-teal-600 bg-teal-50',
};
const SERVICE_LABELS = {
  courses: 'Courses', agents: 'Agents', colleges: 'Colleges', visa: 'Visa Services', jobs: 'Jobs',
  rpl: 'RPL', skills: 'Skills', insurance: 'Insurance', rentals: 'Rentals',
};

const NAV_ITEMS = [
  { path: '/flow', label: 'Flow', icon: Home },
  { path: '/explore', label: 'Explore', icon: Compass },
  { path: '/listings', label: 'Listings', icon: ListTodo, flagKey: 'listings' },
  { path: '/blog', label: 'Blog', icon: BookOpen },
  { path: '/chat', label: 'Chat', icon: MessageSquare },
  { path: '/ai', label: 'Ask Unexa AI', icon: Bot },
];

export default function DesktopLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Role from JWT for synchronous gating — no flash of "Ask Unexa AI"
  // for agents/institutions on page load (Feb 2026).
  const role = user?.role || roleFromToken();
  const aiAllowed = isAiChatAllowed(role);
  const { isEnabled } = usePlatformFlags();
  const applyCtx = useApplyContext();
  const [serviceCounts, setServiceCounts] = useState({});
  const [trendingTags, setTrendingTags] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [aiQuestion, setAiQuestion] = useState('');

  useEffect(() => {
    axios.get(`${API}/explore/feed?page=1&limit=6`).then(r => setServiceCounts(r.data?.service_counts || {})).catch(() => {});
    axios.get(`${API}/hashtags/trending`).then(r => setTrendingTags(r.data || [])).catch(() => {});
    if (user) {
      axios.get(`${API}/social/suggestions`).then(r => setSuggestions(r.data?.slice(0, 5) || [])).catch(() => {});
    }
  }, [user]);

  const currentPath = location.pathname;

  return (
    <div className="max-w-7xl mx-auto px-4 pt-4 animate-fade-in">
      <div className="lg:grid lg:grid-cols-[260px_1fr_300px] lg:gap-6">
        {/* LEFT SIDEBAR */}
        <aside className="hidden lg:block sticky top-20 self-start space-y-4" data-testid="desktop-left-sidebar">
          {user && (
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="sidebar-profile-card">
              <div className="h-16 bg-gradient-to-r from-blue-400 to-teal-400 relative">
                <div className="absolute -bottom-5 left-4">
                  <div className="w-12 h-12 rounded-full bg-white border-[3px] border-white overflow-hidden shadow-md">
                    {user.profile_picture ? (
                      <img src={resolveImageUrl(user.profile_picture)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-blue-50 flex items-center justify-center">
                        <span className="text-sm font-bold text-[#2F80ED]">{user.name?.[0]?.toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="pt-7 px-4 pb-4">
                <p className="font-bold text-sm text-[#0A2540] truncate">{user.name}</p>
                <p className="text-xs text-slate-400 truncate">{user.email}</p>
                {user.headline && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{user.headline}</p>}
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-100 p-3 space-y-0.5" data-testid="sidebar-quick-nav">
            {NAV_ITEMS.map(({ path, label, icon: Icon, flagKey }) => {
              // Respect the PLATFORM-LEVEL feature flag (admin master switch
              // — e.g. /admin/feature-flags toggle for "listings"). This is
              // the right check for nav items that should be globally
              // hidden when the feature is disabled site-wide, including
              // for unauthenticated guests. `usePlatformFlags` works for
              // both guests and authed users, unlike `useFeatureFlags`
              // which only loads after auth. Mar 2, 2026.
              if (flagKey && !isEnabled(flagKey)) return null;
              // Role gate — hide "Ask Unexa AI" from agents/institutions
              // (Feb 2026).
              if (path === '/ai' && !aiAllowed) return null;
              const active = currentPath === path || currentPath.startsWith(path + '/');
              return (
                <button key={path} onClick={() => navigate(path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active ? 'text-[#0A2540] bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`}>
                  <Icon className={`h-4 w-4 ${active ? 'text-[#2F80ED]' : 'text-slate-400'}`} /> {label}
                </button>
              );
            })}
            {user && (() => {
              // Role-aware "My Profile" target:
              //   agent       → /agents/<user_id> (agent public profile)
              //   institution → /university/<university_id> (university page)
              //   student     → /user/<user_id>
              //   admin       → /admin
              //   recruiter   → /recruiter
              // Was unconditionally /user/<user_id> which caused "University
              // not found" toast for institutions and 404 for agents.
              // Mar 2, 2026.
              const profilePath = (
                user.role === 'agent'       ? `/agents/${user.user_id}` :
                user.role === 'institution' ? `/university/${user.university_id}` :
                user.role === 'admin'       ? '/admin' :
                user.role === 'recruiter'   ? '/recruiter' :
                                              `/user/${user.user_id}`
              );
              const active = currentPath.startsWith(profilePath);
              return (
                <button
                  onClick={() => navigate(profilePath)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active ? 'text-[#0A2540] bg-blue-50' : 'text-slate-600 hover:bg-slate-50'}`}
                  data-testid="sidebar-my-profile"
                >
                  <Bookmark className={`h-4 w-4 ${active ? 'text-[#2F80ED]' : 'text-slate-400'}`} /> My Profile
                </button>
              );
            })()}
          </div>

          {/* Ask Unexa AI — Chat Box (hidden for agents/institutions). */}
          {aiAllowed && (
          <div className="bg-gradient-to-br from-[#0A1628] to-[#0F2137] rounded-2xl p-4 relative overflow-hidden" data-testid="sidebar-ai-box">
            <div className="absolute top-0 right-0 w-20 h-20 bg-violet-500/10 rounded-full -translate-y-1/2 translate-x-1/3" />
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                <Bot className="h-4 w-4 text-violet-400" />
              </div>
              <div>
                <p className="text-white text-sm font-bold">Ask Unexa AI</p>
                <p className="text-slate-400 text-[10px]">Your personal adviser</p>
              </div>
            </div>
            <div className="flex gap-2">
              <input type="text" value={aiQuestion} onChange={e => setAiQuestion(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && aiQuestion.trim()) navigate(`/ai?q=${encodeURIComponent(aiQuestion.trim())}`); }}
                placeholder="Ask about visa, PR, courses..."
                className="flex-1 h-9 px-3 rounded-lg bg-white/10 border border-white/10 text-white text-xs placeholder:text-slate-500 focus:outline-none focus:border-violet-400/50"
                data-testid="sidebar-ai-input" />
              <button onClick={() => { if (aiQuestion.trim()) navigate(`/ai?q=${encodeURIComponent(aiQuestion.trim())}`); else navigate('/ai'); }}
                className="w-9 h-9 rounded-lg bg-violet-500 flex items-center justify-center text-white hover:bg-violet-600 transition-colors shrink-0"
                data-testid="sidebar-ai-go">
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          )}

          {user && (
            <div className="bg-gradient-to-br from-[#2F80ED] to-[#0A2540] rounded-2xl p-4 relative overflow-hidden" data-testid="sidebar-journey-banner">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
              <p className="text-blue-200 text-[10px] font-bold uppercase tracking-widest">Your Journey</p>
              <p className="text-white font-bold text-sm mt-1">What are you looking for?</p>
              <div className="flex gap-2 mt-3 flex-wrap">
                {[
                  { label: 'Admission', type: 'admission', flagKey: 'courses' },
                  { label: 'Visa', type: 'visa', flagKey: 'visa' },
                  { label: 'More', type: 'more' },
                ].filter(b => !b.flagKey || isEnabled(b.flagKey)).map(({ label, type }) => (
                  <button key={label} onClick={() => type === 'more' ? navigate('/explore') : applyCtx?.triggerApply(type)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${label === 'Admission' ? 'bg-white text-[#0A2540]' : 'bg-white/15 text-white border border-white/20'}`}
                    data-testid={`sidebar-journey-${label.toLowerCase()}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* CENTER CONTENT */}
        <main className="min-w-0 w-full">
          {children}
        </main>

        {/* RIGHT SIDEBAR */}
        <aside className="hidden lg:block sticky top-20 self-start space-y-4" data-testid="desktop-right-sidebar">
          {/* Explore Services */}
          {Object.keys(serviceCounts).length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="sidebar-explore-services">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Explore Services</p>
                <button onClick={() => navigate('/explore')} className="text-[10px] text-[#2F80ED] font-semibold hover:underline">View All</button>
              </div>
              <div className="space-y-1.5">
                {Object.entries(serviceCounts).filter(([, v]) => v > 0).slice(0, 8).map(([key, count]) => {
                  const Icon = SERVICE_ICONS[key] || Search;
                  const colors = SERVICE_COLORS[key] || 'text-slate-600 bg-slate-50';
                  const label = SERVICE_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1);
                  return (
                    <button key={key} onClick={() => navigate(`/explore?service=${key}`)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
                      data-testid={`sidebar-svc-${key}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-sm font-medium text-[#0A2540] group-hover:text-[#2F80ED] transition-colors">{label}</p>
                      </div>
                      <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Trending */}
          {trendingTags.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="sidebar-trending">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> Trending Now
              </p>
              <div className="space-y-2">
                {trendingTags.slice(0, 6).map(t => (
                  <button key={t.tag} onClick={() => navigate(`/feed?tag=${t.tag}`)}
                    className="w-full text-left px-3 py-2 rounded-xl text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                    data-testid={`sidebar-trend-${t.tag}`}>
                    <span className="font-semibold">#{t.tag}</span>
                    <span className="ml-2 text-xs opacity-60">{t.count} posts</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* People to Follow */}
          {suggestions.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="sidebar-suggestions">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">People to Follow</p>
              <div className="space-y-3">
                {suggestions.slice(0, 4).map(s => (
                  <div key={s.user_id} className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate(`/user/${s.user_id}`)} data-testid={`sidebar-suggest-${s.user_id}`}>
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center overflow-hidden shrink-0">
                      {s.profile_picture ? <img src={resolveImageUrl(s.profile_picture)} alt="" className="w-full h-full object-cover" /> :
                        <span className="text-sm font-bold text-[#2F80ED]">{s.name?.[0]?.toUpperCase()}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0A2540] truncate group-hover:text-[#2F80ED] transition-colors">{s.name}</p>
                      <p className="text-[10px] text-slate-400 truncate">{s.reason}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
